import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `dashboard` domain — the backoffice landing screen.
 *
 * This is the sharpest case the whole help system exists for: ONE screen
 * carries two opposite sign conventions. `houseWinloss` and both week panels
 * negate the rollup (`-today.winloss`, `-r.winloss`) so plus = the house won,
 * while the winners/losers lists render `MemberReportRow.winloss` raw, where
 * plus = the PLAYER won. Every signed field below therefore states which side
 * it is written from, not just which way the arrow points.
 *
 * `partially-wired` for the queue row, not the money: the three queue tiles are
 * not permission-checked and 403 into a rendered 0, and their money lines add up
 * only the first page of pending items. Those lines are one per denomination now
 * (`totalsByCurrency`, shared with the bulk-approve dialog), like the money tiles
 * above them, which come one block per denomination off
 * `dashboardSummary.byCurrency`.
 */
export const dashboardGuide: DomainGuide = {
  domain: 'dashboard',
  pages: [
    {
      id: 'dashboard.home',
      route: '/',
      // Everything above the queue row is gated on report:read. The three queue
      // tiles are NOT gated client-side and 403 into a rendered 0 — see cautions.
      permission: 'report:read',
      status: 'partially-wired',
      fields: [
        { key: 'houseWinloss', kind: 'money-signed' },
        { key: 'turnoverToday', kind: 'money-unsigned' },
        {
          key: 'netInflowToday',
          kind: 'money-signed',
          formula: 'net inflow = approved deposits − approved withdrawals',
        },
        { key: 'activeMembers', kind: 'count' },
        { key: 'deposits', kind: 'money-unsigned' },
        { key: 'withdraws', kind: 'money-unsigned' },
        { key: 'newMembers', kind: 'count' },
        { key: 'firstDeposits', kind: 'count' },
        { key: 'vsYesterday', kind: 'percent' },
        { key: 'winlossWeek', kind: 'money-signed' },
        { key: 'winlossByProvider', kind: 'money-signed' },
        { key: 'topTurnover', kind: 'money-unsigned' },
        { key: 'playerWinloss', kind: 'money-signed' },
        { key: 'pendingDeposits', kind: 'count' },
        { key: 'pendingWithdraws', kind: 'count' },
        { key: 'riskQueue', kind: 'count' },
      ],
      cautionCount: 9,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
